<!-- src/components/modals/CreateStudentModal.vue -->
<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50" @click="close" />

      <!-- Modal -->
      <div
        class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">Add Student</h2>
          <button
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            @click="close"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
          </button>
        </div>

        <!-- Form -->
        <form class="flex-1 overflow-y-auto p-6 space-y-5" @submit.prevent="handleSubmit">
          <!-- Photo Upload -->
          <div class="flex flex-col items-center gap-2">
            <button
              type="button"
              class="relative group"
              @click="triggerFileInput"
            >
              <!-- Preview photo -->
              <div v-if="photoPreviewUrl" class="w-20 h-20 rounded-full overflow-hidden">
                <img :src="photoPreviewUrl" alt="Photo preview" class="w-full h-full object-cover" />
              </div>
              <!-- Placeholder -->
              <div
                v-else
                class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300 group-hover:border-bc-indigo-400 group-hover:bg-bc-indigo-50 transition-colors"
              >
                <font-awesome-icon :icon="['fas', 'camera']" class="text-xl text-gray-400 group-hover:text-bc-indigo-500 transition-colors" />
              </div>
              <!-- Overlay on hover when photo exists -->
              <div
                v-if="photoPreviewUrl"
                class="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <font-awesome-icon :icon="['fas', 'camera']" class="text-white text-lg" />
              </div>
            </button>
            <p class="text-xs text-gray-500">Add photo</p>
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onFileSelected"
            />
          </div>

          <!-- Basic Info -->
          <div class="space-y-4">
            <h3 class="text-sm font-medium text-gray-700">Basic Information</h3>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.firstName"
                  type="text"
                  required
                  placeholder="John"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.lastName"
                  type="text"
                  required
                  placeholder="Smith"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">School ID</label>
                <input
                  v-model="form.schoolId"
                  type="text"
                  placeholder="STU-001"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  v-model="form.gender"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                v-model="form.dob"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="form.email"
                type="email"
                placeholder="student@school.edu"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
              />
              <p class="text-xs text-gray-400 mt-1">Used to invite this student to Connect</p>
            </div>
          </div>

          <!-- More Details (collapsible) -->
          <div class="border-t border-gray-200 pt-4">
            <button
              type="button"
              class="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              @click="showMoreDetails = !showMoreDetails"
            >
              <font-awesome-icon
                :icon="['fas', showMoreDetails ? 'chevron-down' : 'chevron-right']"
                class="text-xs"
              />
              More Details
            </button>

            <div v-if="showMoreDetails" class="mt-4 space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                <input
                  v-model="form.middleName"
                  type="text"
                  placeholder="Michael"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                <input
                  v-model="form.nationality"
                  type="text"
                  placeholder="American"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  v-model="form.mobile"
                  type="tel"
                  placeholder="+1 555-0123"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <!-- Error message -->
          <div
            v-if="error"
            class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            {{ error }}
          </div>
        </form>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <AppButton
            variant="secondary"
            size="md"
            @click="close"
          >
            Cancel
          </AppButton>
          <AppButton
            variant="primary"
            size="md"
            :disabled="!canSave"
            :loading="saving"
            @click="handleSubmit"
          >
            Add Student
          </AppButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { classesApi, studentsApi } from '@/api'
import type { CreateStudentRequest } from '@/api/api'
import { useAnalytics } from '@/composables/useAnalytics'

const props = defineProps<{
  modelValue: boolean
  classId: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [student: { id: string; firstName: string; lastName: string }]
}>()

const { capture } = useAnalytics()

const form = ref<CreateStudentRequest>({
  firstName: '',
  lastName: '',
  middleName: '',
  schoolId: '',
  gender: '',
  dob: '',
  nationality: '',
  email: '',
  mobile: '',
})

const showMoreDetails = ref(false)
const saving = ref(false)
const error = ref<string | null>(null)

// Photo upload
const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const photoPreviewUrl = ref<string | null>(null)

const canSave = computed(() => {
  return form.value.firstName?.trim() && form.value.lastName?.trim()
})

function close() {
  emit('update:modelValue', false)
}

function triggerFileInput() {
  fileInputRef.value?.click()
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    error.value = 'Please select an image file.'
    return
  }

  // Validate file size (5MB)
  if (file.size > 5 * 1024 * 1024) {
    error.value = 'Photo must be less than 5MB.'
    return
  }

  error.value = null
  selectedFile.value = file

  // Revoke previous preview URL
  if (photoPreviewUrl.value) {
    URL.revokeObjectURL(photoPreviewUrl.value)
  }
  photoPreviewUrl.value = URL.createObjectURL(file)
}

function resetForm() {
  form.value = {
    firstName: '',
    lastName: '',
    middleName: '',
    schoolId: '',
    gender: '',
    dob: '',
    nationality: '',
    email: '',
    mobile: '',
  }
  showMoreDetails.value = false
  error.value = null
  selectedFile.value = null
  if (photoPreviewUrl.value) {
    URL.revokeObjectURL(photoPreviewUrl.value)
    photoPreviewUrl.value = null
  }
  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

async function handleSubmit() {
  if (!canSave.value || saving.value) return

  saving.value = true
  error.value = null

  try {
    // Clean up empty strings
    const request: CreateStudentRequest = {
      firstName: form.value.firstName?.trim(),
      lastName: form.value.lastName?.trim(),
    }

    if (form.value.middleName?.trim()) request.middleName = form.value.middleName.trim()
    if (form.value.schoolId?.trim()) request.schoolId = form.value.schoolId.trim()
    if (form.value.gender) request.gender = form.value.gender
    if (form.value.dob) request.dob = form.value.dob
    if (form.value.nationality?.trim()) request.nationality = form.value.nationality.trim()
    if (form.value.email?.trim()) request.email = form.value.email.trim()
    if (form.value.mobile?.trim()) request.mobile = form.value.mobile.trim()

    const response = await classesApi.classesClassIdStudentsPost(props.classId, request)
    const student = response.data as any

    // Upload photo if selected
    if (selectedFile.value && student.id) {
      try {
        await studentsApi.uploadStudentPhoto(student.id, selectedFile.value)
      } catch (photoErr) {
        console.error('Failed to upload student photo:', photoErr)
        // Don't block student creation if photo upload fails
      }
    }

    capture('student_added')
    emit('created', {
      id: student.id || '',
      firstName: student.firstName || '',
      lastName: student.lastName || '',
    })
    close()
  } catch (err: unknown) {
    console.error('Failed to create student:', err)
    error.value = 'Failed to add student. Please try again.'
  } finally {
    saving.value = false
  }
}

// Reset form when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      resetForm()
    }
  }
)
</script>
